/**
 * SmoothScroll
 *
 * Mounts a single Lenis instance for window-level scrolling and replaces the
 * old ScrollToTop component. Excludes routes whose page is its own scroll
 * container (Aether, Builder, Games, EndlessRunner) so Lenis doesn't fight
 * the internal `overflow-y-auto` panels there.
 *
 * Lenis is initialised with autoRaf so it drives its own requestAnimationFrame
 * loop, and we destroy it on unmount / route change to excluded pages.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';

// Routes that have their own internal scroll container — Lenis off here.
// Matches the route OR any nested route (e.g. /shop is window-scroll, but
// /aether and /aether/* are excluded).
const EXCLUDED_PREFIXES = ['/aether', '/builder', '/games', '/en/builder', '/en/games'];

function isExcluded(pathname) {
  return EXCLUDED_PREFIXES.some(p => pathname === p || pathname.startsWith(p + '/'));
}

export default function SmoothScroll() {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);

  // Mount/destroy Lenis based on whether the current route is excluded.
  useEffect(() => {
    if (isExcluded(pathname)) return;

    const lenis = new Lenis({
      autoRaf: true,           // Lenis runs its own rAF loop
      duration: 1.15,          // scrollTo / wheel inertia duration (sec)
      lerp: 0.1,               // 0 = no smoothing, 0.1 = silky, 0.2 = floaty
      smoothWheel: true,
      smoothTouch: false,      // touch is already physical — don't double-smooth
      // Subtle easeOutExpo — feels expensive without being slow
      easing: (t) => 1 - Math.pow(1 - t, 4),
    });
    lenisRef.current = lenis;

    return () => {
      try { lenis.destroy(); } catch { /* noop */ }
      lenisRef.current = null;
    };
  }, [pathname]);

  // Replace the old ScrollToTop: at every route change, jump to top.
  // Use Lenis's instant scrollTo when available so it doesn't conflict with
  // the in-flight smooth animation; otherwise fall back to window scroll.
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}
